import _ from "lodash";

/**
 * Returns a promise that resolves after the specified delay
 * @param ms - Milliseconds to delay
 * @returns Promise that resolves after the delay
 * @example await delay(1000) // waits 1 second
 */
export async function delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Converts a route path pattern to a regular expression
 * @param path - Route path with optional :param patterns
 * @returns RegExp for matching the path
 * @example pathToRegex("/users/:id") matches "/users/123"
 */
export function pathToRegex(path: string): RegExp {
    return new RegExp("^" + path.replace(/:[^\s/]+/g, "([^/]+)").replace(/\//g, "\\/") + "$");
}

/**
 * Generates a UUID v4 using the crypto API
 * @returns A UUID string
 * @example generateUUID() => "550e8400-e29b-41d4-a716-446655440000"
 */
export function generateUUID(): string {
    return crypto.randomUUID();
}

/**
 * Recursively finds the path to the first changed property between two objects
 * @param newData - New data object
 * @param oldData - Old data object
 * @param path - Current path (used internally for recursion)
 * @returns Dot-separated path to the changed property, or null if no changes
 * @example findChangedPath({ a: { b: 2 } }, { a: { b: 1 } }) => "a.b"
 */
export function findChangedPath(newData: any, oldData: any, path: string = ""): string | null {
    if (_.isEqual(newData, oldData)) return null;

    if (Array.isArray(newData)) {
        if (!Array.isArray(oldData) || newData.length !== oldData.length) return path;
        for (let i = 0; i < newData.length; i++) {
            const newPath = `${path}[${i}]`;
            const result = findChangedPath(newData[i], oldData[i], newPath);
            if (result) return result;
        }
    } else if (typeof newData === "object" && newData !== null) {
        for (const key in newData) {
            if (Object.prototype.hasOwnProperty.call(newData, key)) {
                const newPath = path ? `${path}.${key}` : key;
                if (!oldData || !Object.prototype.hasOwnProperty.call(oldData, key)) return newPath;
                const result = findChangedPath(newData[key], oldData[key], newPath);
                if (result) return result;
            }
        }
    } else return path;

    return null;
}

/**
 * Generates a future Unix timestamp
 * @param days - Number of days in the future
 * @returns Unix timestamp in seconds
 * @example generateFutureDate(7) => Unix timestamp 7 days from now
 */
export function generateFutureDate(days: number): number {
    const currentDate = new Date();
    const futureDate = addFewDays(currentDate, days);
    futureDate.setSeconds(0);
    futureDate.setMilliseconds(0);
    return Math.floor(futureDate.getTime() / 1000);
}

/**
 * Helper function to add days to a date
 * @param date - Starting date
 * @param days - Number of days to add
 * @returns New date with days added
 */
function addFewDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}

/**
 * Recursively trims all string values in an object, array, or primitive
 * @param something - The data structure to trim
 * @returns Deep copy with all strings trimmed
 * @example trimStringsFromAnywhere({ name: "  John  " }) => { name: "John" }
 */
export function trimStringsFromAnywhere<T>(something: T): T {
    // Defensive check: return as-is for undefined/null to prevent JSON.parse errors
    if (something === undefined || something === null) {
        return something;
    }

    let newThing = JSON.parse(JSON.stringify(something));

    function trimHelper(item: any): any {
        if (typeof item === "string") {
            return item.trim();
        } else if (Array.isArray(item)) {
            return item.map(trimHelper);
        } else if (item && typeof item === "object") {
            for (const key in item) {
                if (Object.prototype.hasOwnProperty.call(item, key)) {
                    item[key] = trimHelper(item[key]);
                }
            }
        }
        return item;
    }

    return trimHelper(newThing);
}
